import { t } from "../../i18n";
import { ExpandableOutput } from "../ExpandableOutput";
import { CalculateResult } from "../chat/CalculateResult";
import { SearchResult } from "../chat/SearchResult";
import { FetchResult } from "../chat/FetchResult";
import { MessageResult } from "../chat/MessageResult";
import { ArtifactResult } from "../artifacts/ArtifactResult";
import { GeneratedActivityResult } from "../chat/GeneratedActivityResult";
import { GenericResult } from "../chat/GenericResult";
import { OperationResult } from "../chat/OperationResult";
import { AsyncCommandResult } from "../chat/AsyncCommandResult";
import { isAsyncCommand } from "../chat/AsyncCommandResult.utils";
import { ShellResult } from "../chat/ShellResult";
import { WorkspaceResult } from "../chat/WorkspaceResult";
import { RawDetails } from "../RawDetails";
import {
  asRecord,
  constrainsOutput,
  displayName,
  stringValue,
} from "../chat/ToolResultView.utils";
import { XCircle } from "lucide-react";

export { ArtifactResult } from "../artifacts/ArtifactResult";

type ToolResultViewProps = {
  name: string;
  argumentsValue: unknown;
  result: unknown;
  error?: string;
  status?: string;
};

export function ToolResultView({
  name,
  argumentsValue,
  result,
  error,
  status,
}: ToolResultViewProps) {
  const content = isAsyncCommand(name, result) ? (
    <AsyncCommandResult
      name={name}
      argumentsValue={argumentsValue}
      result={result}
      error={error}
      status={status}
    />
  ) : name === "agent_workspace_shell" ? (
    <ShellResult
      argumentsValue={argumentsValue}
      result={result}
      error={error}
      status={status}
    />
  ) : error ? (
    <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50/70 p-3 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/20 dark:text-red-300">
      <XCircle size={16} className="mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold">{t("Tool failed")}</div>
        <div className="mt-1 leading-5 whitespace-pre-wrap">{error}</div>
      </div>
    </div>
  ) : name === "calculate" ? (
    <CalculateResult result={result} />
  ) : name === "ollama_web_search" || name === "tavily_web_search" ? (
    <SearchResult
      result={result}
      query={stringValue(asRecord(argumentsValue)?.query)}
    />
  ) : name === "ollama_web_fetch" || name === "tavily_web_fetch" ? (
    <FetchResult result={result} />
  ) : name === "send_email_notification" || name === "send_whatsapp_message" ? (
    <MessageResult
      name={name}
      result={result}
      argumentsValue={argumentsValue}
    />
  ) : name === "generate_activity" ? (
    <GeneratedActivityResult result={result} />
  ) : name === "artifact_vault" || name === "document_studio" ? (
    <ArtifactResult result={result} />
  ) : [
      "agent_workspace_upload_artifact",
      "workspace_list",
      "workspace_read",
      "workspace_edit",
      "workspace_overwrite",
      "workspace_write",
      "workspace_patch",
      "workspace_rg",
      "workspace_glob",
      "proxmox_target_status",
      "proxmox_target_resources",
      "proxmox_inventory",
      "proxmox_target_power",
    ].includes(name) ? (
    <WorkspaceResult
      name={name}
      argumentsValue={argumentsValue}
      result={result}
    />
  ) : [
      "personalize_lumen",
      "request_business_action_approval",
      "publish_marketing_content",
      "send_marketing_email",
      "run_marketing_report",
      "manage_marketing_lead",
      "manage_business_integration",
    ].includes(name) ? (
    <OperationResult
      name={name}
      argumentsValue={argumentsValue}
      result={result}
      status={status}
    />
  ) : (
    <GenericResult value={result} />
  );
  const renderedContent = constrainsOutput(name) ? (
    <ExpandableOutput>{content}</ExpandableOutput>
  ) : (
    content
  );
  return (
    <div className="space-y-2">
      {renderedContent}
      <div className="flex items-center gap-2">
        {name !== "agent_workspace_shell" && (
          <RawDetails
            label={`${displayName(name)} input`}
            value={argumentsValue}
          />
        )}
        {name !== "ollama_web_fetch" &&
          name !== "tavily_web_fetch" &&
          name !== "agent_workspace_shell" && (
            <RawDetails label={t("Developer JSON")} value={result} />
          )}
      </div>
    </div>
  );
}
